import React, { useState, useRef, useEffect } from 'react';

/**
 * A component for inputting multiple email addresses as tags
 * @param {Object} props - Component props
 * @param {string[]} props.emails - Array of email addresses
 * @param {Function} props.onChange - Function to call when emails change
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {boolean} props.required - Whether the input is required
 * @param {string} props.error - Error message to display
 */
const EmailTagInput = ({ 
  emails = [], 
  onChange, 
  className = '', 
  placeholder = 'Enter email address(es)', 
  disabled = false,
  required = false,
  error = ''
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Handle key down events
  const handleKeyDown = (e) => {
    if (disabled) return;
    
    // If Enter or comma is pressed
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
    // If Backspace is pressed and input is empty, remove the last email
    else if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      removeEmail(emails.length - 1);
    }
  };

  // Handle blur event
  const handleBlur = () => {
    setIsFocused(false);
    if (inputValue.trim()) {
      addEmail();
    }
  };

  // Add email to the list
  const addEmail = () => {
    const trimmedEmail = inputValue.trim().replace(/,$/, ''); // Remove trailing comma if any
    
    if (trimmedEmail && emailRegex.test(trimmedEmail) && !emails.includes(trimmedEmail)) {
      const newEmails = [...emails, trimmedEmail];
      onChange(newEmails);
      setInputValue('');
    } else if (trimmedEmail && !emailRegex.test(trimmedEmail)) {
      // Handle invalid email
      // Could add visual feedback here
    }
  };

  // Remove email from the list
  const removeEmail = (index) => {
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    onChange(newEmails);
  };

  // Handle click on container to focus input
  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle paste event to add multiple emails at once
  const handlePaste = (e) => {
    if (disabled) return;
    
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[,;\s]+/).filter(Boolean); // Split by comma, semicolon, or whitespace
    
    const validEmails = pastedEmails.filter(email => emailRegex.test(email) && !emails.includes(email));
    
    if (validEmails.length) {
      const newEmails = [...emails, ...validEmails];
      onChange(newEmails);
    }
  };

  // Convert emails array to comma-separated string for backward compatibility
  useEffect(() => {
    // This is a side effect to handle the case where the component receives a string instead of an array
    if (typeof emails === 'string' && emails) {
      const emailArray = emails.split(',').map(e => e.trim()).filter(Boolean);
      if (JSON.stringify(emailArray) !== JSON.stringify(emails)) {
        onChange(emailArray);
      }
    }
  }, [emails, onChange]);

  // Determine container classes
  const containerClasses = `flex flex-wrap items-center border rounded-md p-1 ${
    isFocused ? 'border-blue-500' : 'border-gray-300'
  } ${disabled ? 'bg-gray-100' : 'bg-white'} ${className}`;

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      onClick={handleContainerClick}
    >
      {/* Email tags */}
      {emails.map((email, index) => (
        <div
          key={index}
          className={`flex items-center bg-blue-100 text-blue-800 rounded-md px-2 py-1 m-1 ${
            disabled ? 'opacity-70' : ''
          }`}
        >
          <span className="text-sm mr-1">{email}</span>
          {!disabled && (
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 focus:outline-none"
              onClick={() => removeEmail(index)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      ))}

      {/* Input field */}
      <input
        ref={inputRef}
        type="text"
        className={`flex-grow outline-none px-2 py-1 min-w-[100px] ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onPaste={handlePaste}
        placeholder={emails.length === 0 ? placeholder : ''}
        disabled={disabled}
        required={required && emails.length === 0}
        aria-invalid={!!error}
      />

      {/* Error message */}
      {error && (
        <div className="w-full text-red-500 text-xs mt-1">{error}</div>
      )}
    </div>
  );
};

export default EmailTagInput;
